import React, { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CircularProgress from "@mui/material/CircularProgress";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Avatar from "@mui/material/Avatar";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import PeopleIcon from "@mui/icons-material/People";
import PushPinIcon from "@mui/icons-material/PushPin";
import CampaignIcon from "@mui/icons-material/Campaign";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import AudioFileIcon from "@mui/icons-material/AudioFile";
import TextFieldsIcon from "@mui/icons-material/TextFields";

function DashboardPage() {
  const { currentUser } = useAuth();
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalPins: 0,
    totalCampaigns: 0,
    activeUsers: 0,
    totalLikes: 0,
    totalComments: 0,
    recentPinsCount: 0,
  });
  const [recentPins, setRecentPins] = useState([]);
  const [pinTypeStats, setPinTypeStats] = useState({
    audio: 0,
    text: 0,
    unknown: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setError(null);
        setLoading(true);

        const { apiCall } = await import("../config/api");

        // Dashboard verilerini API'den çek
        const dashboardData = await apiCall("/api/admin/dashboard", {
          method: "GET",
        });

        setStats({
          totalUsers: dashboardData.stats?.totalUsers || 0,
          totalPins: dashboardData.stats?.totalPins || 0,
          totalCampaigns: dashboardData.stats?.totalCampaigns || 0,
          activeUsers: dashboardData.stats?.activeUsers || 0,
          totalLikes: dashboardData.stats?.totalLikes || 0,
          totalComments: dashboardData.stats?.totalComments || 0,
          recentPinsCount: dashboardData.stats?.recentPinsCount || 0,
        });

        setRecentPins(dashboardData.recentPins || []);
        setPinTypeStats(
          dashboardData.pinTypeStats || { audio: 0, text: 0, unknown: 0 }
        );
      } catch (err) {
        console.error("Gösterge paneli verileri çekilirken hata:", err);
        setError(`Veriler yüklenemedi: ${err.message}`);
        setStats({
          totalUsers: 0,
          totalPins: 0,
          totalCampaigns: 0,
          activeUsers: 0,
          totalLikes: 0,
          totalComments: 0,
          recentPinsCount: 0,
        });
        setRecentPins([]);
        setPinTypeStats({ audio: 0, text: 0, unknown: 0 });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "calc(100vh - 128px)",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
          Gösterge Paneli
        </Typography>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  const formatDate = (dateString) => {
    if (!dateString) return "Tarih Yok";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "Geçersiz Tarih";
      return date.toLocaleString("tr-TR");
    } catch (e) {
      return "Geçersiz Tarih";
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      {currentUser && (
        <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>
          Hoş geldiniz, {currentUser.displayName || currentUser.email}!
        </Typography>
      )}

      {/* Main Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {/* Toplam Kullanıcı */}
        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              height: "100%",
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            }}
          >
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Box>
                  <Typography
                    variant="h3"
                    sx={{
                      color: "white",
                      fontWeight: "bold",
                      textShadow:
                        "0 1px 3px rgba(0,0,0,0.3), 0 0 1px rgba(0,0,0,0.5)",
                    }}
                  >
                    {stats.totalUsers}
                  </Typography>
                  <Typography
                    sx={{
                      color: "white",
                      textShadow: "0 1px 2px rgba(0,0,0,0.3)",
                    }}
                  >
                    Toplam Kullanıcı
                  </Typography>
                  {stats.activeUsers > 0 && (
                    <Typography
                      variant="caption"
                      sx={{
                        color: "white",
                        textShadow: "0 1px 2px rgba(0,0,0,0.3)",
                      }}
                    >
                      {stats.activeUsers} aktif (30 gün)
                    </Typography>
                  )}
                </Box>
                <PeopleIcon
                  sx={{ fontSize: 60, color: "rgba(255,255,255,0.3)" }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Toplam Pin */}
        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              height: "100%",
              background: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
            }}
          >
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Box>
                  <Typography
                    variant="h3"
                    sx={{
                      color: "white",
                      fontWeight: "bold",
                      textShadow:
                        "0 1px 3px rgba(0,0,0,0.3), 0 0 1px rgba(0,0,0,0.5)",
                    }}
                  >
                    {stats.totalPins}
                  </Typography>
                  <Typography
                    sx={{
                      color: "white",
                      textShadow: "0 1px 2px rgba(0,0,0,0.3)",
                    }}
                  >
                    Toplam Pin
                  </Typography>
                  {stats.recentPinsCount > 0 && (
                    <Typography
                      variant="caption"
                      sx={{
                        color: "white",
                        textShadow: "0 1px 2px rgba(0,0,0,0.3)",
                      }}
                    >
                      {stats.recentPinsCount} yeni (7 gün)
                    </Typography>
                  )}
                </Box>
                <PushPinIcon
                  sx={{ fontSize: 60, color: "rgba(255,255,255,0.3)" }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Toplam Kampanya */}
        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              height: "100%",
              background: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
            }}
          >
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Box>
                  <Typography
                    variant="h3"
                    sx={{
                      color: "white",
                      fontWeight: "bold",
                      textShadow:
                        "0 1px 3px rgba(0,0,0,0.3), 0 0 1px rgba(0,0,0,0.5)",
                    }}
                  >
                    {stats.totalCampaigns}
                  </Typography>
                  <Typography
                    sx={{
                      color: "white",
                      textShadow: "0 1px 2px rgba(0,0,0,0.3)",
                    }}
                  >
                    Toplam Kampanya
                  </Typography>
                </Box>
                <CampaignIcon
                  sx={{ fontSize: 60, color: "rgba(255,255,255,0.3)" }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Etkileşim */}
        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              height: "100%",
              background: "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)",
            }}
          >
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Box>
                  <Typography
                    variant="h3"
                    sx={{
                      color: "white",
                      fontWeight: "bold",
                      textShadow:
                        "0 1px 3px rgba(0,0,0,0.3), 0 0 1px rgba(0,0,0,0.5)",
                    }}
                  >
                    {stats.totalLikes + stats.totalComments}
                  </Typography>
                  <Typography
                    sx={{
                      color: "white",
                      textShadow: "0 1px 2px rgba(0,0,0,0.3)",
                    }}
                  >
                    Toplam Etkileşim
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "white",
                      textShadow: "0 1px 2px rgba(0,0,0,0.3)",
                    }}
                  >
                    {stats.totalLikes} beğeni, {stats.totalComments} yorum
                  </Typography>
                </Box>
                <TrendingUpIcon
                  sx={{ fontSize: 60, color: "rgba(255,255,255,0.3)" }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Secondary Stats */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {/* Pin Tür Dağılımı */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Pin Tür Dağılımı
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <AudioFileIcon color="primary" />
                    <Typography>Sesli</Typography>
                  </Box>
                  <Chip label={pinTypeStats.audio} color="primary" />
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <TextFieldsIcon color="secondary" />
                    <Typography>Metin</Typography>
                  </Box>
                  <Chip label={pinTypeStats.text} color="secondary" />
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography>Bilinmeyen</Typography>
                  </Box>
                  <Chip label={pinTypeStats.unknown} />
                </Box>
                <Divider />
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography fontWeight="bold">Toplam</Typography>
                  <Typography fontWeight="bold">
                    {pinTypeStats.audio +
                      pinTypeStats.text +
                      pinTypeStats.unknown}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Son Eklenen Pinler */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Son Eklenen Pinler
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <List sx={{ maxHeight: 400, overflow: "auto" }}>
                {recentPins.length === 0 ? (
                  <ListItem>
                    <ListItemText primary="Henüz pin bulunmuyor" />
                  </ListItem>
                ) : (
                  recentPins.map((pin, index) => (
                    <React.Fragment key={pin.id}>
                      <ListItem alignItems="flex-start">
                        <Avatar
                          src={pin.creatorPhotoURL}
                          sx={{ mr: 2, bgcolor: "primary.main" }}
                        >
                          {pin.creatorInfo?.charAt(0)?.toUpperCase() || "?"}
                        </Avatar>
                        <ListItemText
                          primary={
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <Typography variant="body1" fontWeight="medium">
                                {pin.title}
                              </Typography>
                              {pin.audioUrl && (
                                <Chip
                                  icon={<AudioFileIcon />}
                                  label="Ses"
                                  size="small"
                                  color="primary"
                                />
                              )}
                            </Box>
                          }
                          secondary={
                            <React.Fragment>
                              <Typography
                                component="span"
                                variant="body2"
                                color="text.primary"
                              >
                                Ekleyen: {pin.creatorInfo}
                              </Typography>
                              {" — "}
                              {formatDate(pin.createdAt)}
                            </React.Fragment>
                          }
                        />
                      </ListItem>
                      {index < recentPins.length - 1 && (
                        <Divider variant="inset" component="li" />
                      )}
                    </React.Fragment>
                  ))
                )}
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}

export default DashboardPage;
